import { SINGLE_MODIFIERS_GROUP_ID } from "#/constants/modifiers";
import type { ISelectModifiers, ISizes } from "#/types/iCardProduct";
import { calculateProductNutritions, toCartNutritionFields } from "#/utils/nutritions";
import { findModifierSizeForDish } from "#/utils/syncModifierWithSize";

const resolveModifierSizeId = (modifier: ISelectModifiers, dishSizeId?: string) => {
	if (Array.isArray(modifier.size)) return modifier.size.map((s: { id?: string }) => s.id).filter(Boolean);
	if (modifier.size?.id) return modifier.size.id;

	return findModifierSizeForDish(modifier, dishSizeId ? ({ id: dishSizeId } as ISizes) : null)?.id;
};

const mapModifierForCart = (modifier: ISelectModifiers, dishSizeId?: string) => ({
	...modifier,
	old_price: modifier.price,
	size: resolveModifierSizeId(modifier, dishSizeId) as ISizes | null | undefined,
	is_marked: !!modifier.is_marked,
	marking_codes: modifier.marking_codes ?? null,
});

/** Сборка payload для mainStore.addToCart — как в ButtonPriceCount.addProductToCart. */
export const buildCartProductFromCardState = (stateCard: {
	openCard: NonNullable<{
		id: string;
		is_fake?: boolean;
		is_od_united_product?: boolean;
		marking_code?: string | null;
		adult?: boolean;
		is_marked?: boolean;
		amount: number;
		name: string;
		loc_name?: Record<string, string | null>;
		image_link?: string;
		price: number;
		sizes?: ISizes[];
		groups: string[];
		energy_amount?: number;
		proteins_amount?: number;
		fat_amount?: number;
		carbohydrates_amount?: number;
		weight?: number | null;
		portion_nutrition?: {
			weight: number | null;
			measure_unit?: string | null;
			fat_amount: number | null;
			proteins_amount: number | null;
			carbohydrates_amount: number | null;
			energy_amount: number | null;
		} | null;
	}>;
	selectedSize?: ISizes | null;
	groupModifiersSelected: {
		group: string;
		is_fake?: boolean;
		is_product_determining?: boolean;
		free_quantity?: number | null;
		modifiers: ISelectModifiers[];
	}[];
	groupModifiers?: { id: string; free_quantity?: number | null }[];
}) => {
	const modifiers: ISelectModifiers[] = [];
	const group_modifiers: {
		group: string;
		is_fake: boolean;
		is_product_determining: boolean;
		free_quantity: number | null;
		modifiers: ReturnType<typeof mapModifierForCart>[];
	}[] = [];

	const dishSizeId = stateCard.selectedSize?.id;
	const groupDefs = stateCard.groupModifiers;

	for (const group of stateCard.groupModifiersSelected ?? []) {
		const selectedModifiers = group.modifiers ?? [];
		if (group.group === SINGLE_MODIFIERS_GROUP_ID) {
			modifiers.push(...selectedModifiers.map((m) => mapModifierForCart(m, dishSizeId)));
		} else if (selectedModifiers.length > 0) {
			group_modifiers.push({
				group: group.group,
				is_fake: group.is_fake ?? false,
				is_product_determining: group.is_product_determining ?? false,
				free_quantity: group.free_quantity ?? groupDefs?.find((def) => def.id === group.group)?.free_quantity ?? null,
				modifiers: selectedModifiers.map((m) => mapModifierForCart(m, dishSizeId)),
			});
		}
	}

	const openCard = stateCard.openCard;
	const nutritions = toCartNutritionFields(
		calculateProductNutritions({
			product: openCard,
			selectedSize: stateCard.selectedSize,
			groupModifiersSelected: stateCard.groupModifiersSelected,
			amount: 1,
		}),
	);

	return {
		id: openCard.id,
		is_fake: openCard.is_fake ?? false,
		is_od_united_product: openCard.is_od_united_product ?? false,
		adult: openCard.adult ?? false,
		is_marked: !!openCard.is_marked,
		marking_code: openCard.marking_code ?? null,
		amount: openCard.amount,
		name: openCard.name,
		loc_name: openCard.loc_name,
		image_link: openCard.image_link,
		price: openCard.price,
		old_price: openCard.price,
		size: stateCard.selectedSize?.id ?? openCard.sizes?.find((s) => s.is_default)?.id,
		sizes: openCard.sizes || [],
		portion_nutrition: openCard.portion_nutrition ?? null,
		modifiers,
		groups: openCard.groups,
		group_modifiers,
		...nutritions,
	};
};
